import React, { useEffect, useRef, useState, useCallback } from 'react';
import iconTitle from '../../images/icon_title_left.png';

interface FoodData {
    name: string;
    bbox_2d: number[]; // [x_min, y_min, x_max, y_max] 归一化坐标 0.0-1.0
    nutrition: {
        carbs: number;
        protein: number;
        fat: number;
    };
}

interface FoodNutritionLabelProps {
    imageUrl: any;
    foodData: {
        foods: FoodData[];
    };
    scale?: number;
}

interface Size {
    width: number;
    height: number;
}

interface AvoidPoint {
    point: { x: number; y: number };
    type: 'center' | 'corner';
}

interface ArcInfo {
    start: { x: number; y: number };
    end: { x: number; y: number };
    control: { x: number; y: number };
}

interface LabelCandidate {
    point2: { x: number; y: number };
    labelRect: { left: number; top: number; width: number; height: number };
}

// 常量定义
const LABEL_SIZE = { width: 96, height: 50 }; // 食物标签的固定尺寸(宽度96px, 高度50px)
const LABEL_PADDING = 8; // 内边距
const MIN_DISTANCE_FROM_POINT = 20;// 标签与食物中心点之间的最小距离(px)
const ARROW_LENGTH = 10; // 连接箭头的长度(px)
const ARROW_ANGLE = Math.PI / 5;// 箭头两侧的角度(36度)
const LABEL_BG_COLOR = '#F4F0ED';// 标签背景颜色
const TITLE_TEXT_COLOR = '#333333';// 食物名称文字颜色
const NUTRITION_TEXT_COLOR = '#AC722C';// 营养成分文字颜色
const ARC_COLOR = '#CCA87F';// 连接弧线颜色
const TITLE_ICON_HEIGHT = 10;// 标题图标的高度(px)
const ICON_TEXT_SPACING = 2;// 图标与文字之间的间距(px)
const TITLE_NUTRITION_SPACING = 5;// 标题与营养信息之间的垂直间距(px)
const NUTRITION_ITEM_SPACING = 4;// 营养成分各项之间的水平间距(px)
const MIN_LABEL_TO_LABEL_DISTANCE = 30;// 标签间的最小安全距离(px)
const LABEL_DISTANCE_SCORE_WEIGHT = 2; // 距离评分权重系数
const HIGH_DPI_SCALE = window.devicePixelRatio || 1;

export const FoodNutritionLabel: React.FC<FoodNutritionLabelProps> = ({ imageUrl, foodData, scale }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [titleIcon, setTitleIcon] = useState<HTMLImageElement | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [originalImageSize, setOriginalImageSize] = useState<Size>({ width: 0, height: 0 });

    // 加载主图片
    useEffect(() => {
        if (!imageUrl) return;

        // 创建图片对象
        const img = document.createElement('img');
        img.src = imageUrl;

        img.onload = () => {
            setImage(img);
            setOriginalImageSize({ width: img.width, height: img.height });
            setIsLoading(false);
            setHasError(false);
        };

        img.onerror = () => {
            setIsLoading(false);
            setHasError(true);
        };
    }, [imageUrl]);

    // 加载标题图标
    useEffect(() => {
        const loadTitleIcon = async () => {
            try {
                const icon = new Image();
                icon.onload = () => setTitleIcon(icon);
                icon.onerror = () => console.warn('标题图标加载失败');
                icon.src = iconTitle;
            } catch (e) {
                console.warn('标题图标加载失败:', e);
            }
        };

        loadTitleIcon();
    }, []);

    // 绘制逻辑
    const drawCanvas = useCallback(() => {
        if (!canvasRef.current || !image) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 设置高DPI支持
        ctx.scale(HIGH_DPI_SCALE, HIGH_DPI_SCALE);

        // 调整父容器尺寸计算
        const parentSize = {
            width: canvas.width / HIGH_DPI_SCALE,
            height: canvas.height / HIGH_DPI_SCALE
        };
        // 计算图片显示尺寸
        const imageAspectRatio = originalImageSize.width / originalImageSize.height;
        const parentAspectRatio = parentSize.width / parentSize.height;
        const actualScale = scale || 1;
        let displayWidth: number, displayHeight: number;
        if (imageAspectRatio > parentAspectRatio) {
            displayWidth = parentSize.width * actualScale;
            displayHeight = (parentSize.width / imageAspectRatio) * actualScale;
        } else {
            displayHeight = parentSize.height * actualScale;
            displayWidth = (parentSize.height * imageAspectRatio) * actualScale;
        }

        const scaleX = displayWidth / originalImageSize.width;
        const scaleY = displayHeight / originalImageSize.height;
        const imageOffsetX = (parentSize.width - displayWidth) / 2;
        const imageOffsetY = (parentSize.height - displayHeight) / 2;

        // 清空画布
        ctx.clearRect(0, 0, parentSize.width, parentSize.height);

        // 绘制图片
        ctx.drawImage(image, imageOffsetX, imageOffsetY, displayWidth, displayHeight);

        // 存储已放置的标签和避让点
        const placedLabels: Array<{ left: number; top: number; width: number; height: number }> = [];
        const allAvoidPoints: AvoidPoint[] = [];
        const drawnArcs: ArcInfo[] = [];

        // 预处理所有食物的避让点
        foodData.foods.forEach(food => {
            const bbox = food.bbox_2d;
            // 转换为原始像素坐标
            const x1Pixel = bbox[0] * originalImageSize.width;
            const y1Pixel = bbox[1] * originalImageSize.height;
            const x2Pixel = bbox[2] * originalImageSize.width;
            const y2Pixel = bbox[3] * originalImageSize.height;
            // 转换为画布坐标
            const center = {
                x: (x1Pixel + x2Pixel) / 2 * scaleX + imageOffsetX,
                y: (y1Pixel + y2Pixel) / 2 * scaleY + imageOffsetY
            };
            const corner1 = { x: x1Pixel * scaleX + imageOffsetX, y: y1Pixel * scaleY + imageOffsetY };
            const corner2 = { x: x2Pixel * scaleX + imageOffsetX, y: y1Pixel * scaleY + imageOffsetY };
            const corner3 = { x: x1Pixel * scaleX + imageOffsetX, y: y2Pixel * scaleY + imageOffsetY };
            const corner4 = { x: x2Pixel * scaleX + imageOffsetX, y: y2Pixel * scaleY + imageOffsetY };

            allAvoidPoints.push(
                { point: center, type: 'center' },
                { point: corner1, type: 'corner' },
                { point: corner2, type: 'corner' },
                { point: corner3, type: 'corner' },
                { point: corner4, type: 'corner' }
            );
        });

        // 辅助函数：计算距离
        const distance = (p1: { x: number; y: number }, p2: { x: number; y: number }) => {
            return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
        };

        // 辅助函数：检查矩形是否包含点
        const rectContainsPoint = (
            rect: { left: number; top: number; width: number; height: number },
            point: { x: number; y: number }
        ) => {
            return point.x >= rect.left && point.x <= rect.left + rect.width &&
                point.y >= rect.top && point.y <= rect.top + rect.height;
        };

        // 辅助函数：检查矩形是否在父容器内
        const isRectWithinParent = (rect: { left: number; top: number; width: number; height: number }) => {
            return rect.left >= 0 && rect.left + rect.width <= parentSize.width &&
                rect.top >= 0 && rect.top + rect.height <= parentSize.height;
        };

        // 辅助函数：检查矩形是否重叠
        const isRectOverlapping = (rect: { left: number; top: number; width: number; height: number }) => {
            return placedLabels.some(placed => {
                return !(rect.left + rect.width < placed.left ||
                    placed.left + placed.width < rect.left ||
                    rect.top + rect.height < placed.top ||
                    placed.top + placed.height < rect.top);
            });
        };

        // 辅助函数：检查贝塞尔曲线与矩形相交
        const doesRectIntersectBezier = (
            rect: { left: number; top: number; width: number; height: number },
            start: { x: number; y: number },
            end: { x: number; y: number },
            control: { x: number; y: number }
        ) => {
            const samples = 20;
            for (let i = 0; i <= samples; i++) {
                const t = i / samples;
                const x = (1 - t) ** 2 * start.x + 2 * (1 - t) * t * control.x + t ** 2 * end.x;
                const y = (1 - t) ** 2 * start.y + 2 * (1 - t) * t * control.y + t ** 2 * end.y;
                if (rectContainsPoint(rect, { x, y })) {
                    return true;
                }
            }
            return false;
        };

        // 辅助函数：计算候选位置得分
        const calculateCandidateScore = (
            labelRect: { left: number; top: number; width: number; height: number },
            targetPoint: { x: number; y: number }
        ) => {
            let score = 100;

            // 检查避让点
            allAvoidPoints.forEach(avoidPoint => {
                if (avoidPoint.point.x === targetPoint.x && avoidPoint.point.y === targetPoint.y) return;
                if (rectContainsPoint(labelRect, avoidPoint.point)) {
                    score -= avoidPoint.type === 'center' ? 50 : 25;
                }
            });

            // 检查与已绘制弧线的相交
            for (const arc of drawnArcs) {
                if (doesRectIntersectBezier(labelRect, arc.start, arc.end, arc.control)) {
                    score -= 20;
                    break;
                }
            }

            // 检查与其他标签的距离
            if (placedLabels.length > 0) {
                let minDistanceToOtherLabel = Infinity;
                const labelCenter = {
                    x: labelRect.left + labelRect.width / 2,
                    y: labelRect.top + labelRect.height / 2
                };

                placedLabels.forEach(placedRect => {
                    const placedCenter = {
                        x: placedRect.left + placedRect.width / 2,
                        y: placedRect.top + placedRect.height / 2
                    };
                    const dist = distance(labelCenter, placedCenter);
                    if (dist < minDistanceToOtherLabel) {
                        minDistanceToOtherLabel = dist;
                    }
                });

                if (minDistanceToOtherLabel < MIN_LABEL_TO_LABEL_DISTANCE) {
                    const distanceDeficit = MIN_LABEL_TO_LABEL_DISTANCE - minDistanceToOtherLabel;
                    score -= Math.floor(distanceDeficit * LABEL_DISTANCE_SCORE_WEIGHT);
                } else {
                    // 增强距离奖励机制
                    const bonus = Math.min(20, (minDistanceToOtherLabel / MIN_LABEL_TO_LABEL_DISTANCE) * 10);
                    score += Math.floor(bonus);
                }
            }
            const labelCenter = {
                x: labelRect.left + labelRect.width / 2,
                y: labelRect.top + labelRect.height / 2
            };
            const distToTarget = distance(labelCenter, targetPoint);

            // 距离目标点越近得分越高（最多+30分）
            const distanceBonus = Math.max(0, 30 - distToTarget / 10);
            score += distanceBonus;

            // 5. 偏好在目标点上方的位置
            // if (labelCenter.y < targetPoint.y) {
            //     score += 15; // 上方位置额外加分
            // }

            // 6. 避免标签超出画布边界
            let boundaryPenalty = 0;
            if (labelRect.left < 10) boundaryPenalty += 10;
            if (labelRect.top < 10) boundaryPenalty += 10;
            if (labelRect.left + labelRect.width > parentSize.width - 10) boundaryPenalty += 10;
            if (labelRect.top + labelRect.height > parentSize.height - 10) boundaryPenalty += 10;
            score -= boundaryPenalty;

            return score;
        };

        // 辅助函数：从point4计算标签矩形（支持动态宽度）
        const calculateLabelRectFromPoint4 = (
            point4: { x: number; y: number },
            point3: { x: number; y: number },
            food: FoodData
        ) => {
            const direction = { x: point4.x - point3.x, y: point4.y - point3.y };
            const dist = distance(point4, point3);

            if (dist < MIN_DISTANCE_FROM_POINT) return null;

            // 动态计算标签宽度
            let dynamicWidth = LABEL_SIZE.width;
            let dynamicHeight = LABEL_SIZE.height;

            const ctx = canvasRef.current?.getContext('2d');
            if (ctx) {
                // 计算食物名称宽度
                ctx.font = '12px SourceHanSerifCN, sans-serif';
                const nameWidth = ctx.measureText(food.name).width;

                // 计算营养信息总宽度
                const nutrition = food.nutrition;
                const carbsText = `碳 ${nutrition.carbs}g`;
                const proteinText = `蛋 ${nutrition.protein}g`;
                const fatText = `脂 ${nutrition.fat}g`;

                ctx.font = '8px sans-serif';
                const carbsWidth = ctx.measureText(carbsText).width;
                const proteinWidth = ctx.measureText(proteinText).width;
                const fatWidth = ctx.measureText(fatText).width;

                const nutritionTotalWidth = carbsWidth + proteinWidth + fatWidth +
                    2 * NUTRITION_ITEM_SPACING;

                // 取最大宽度加上内边距
                const maxWidth = Math.max(nameWidth, nutritionTotalWidth);
                dynamicWidth = Math.max(LABEL_SIZE.width, maxWidth + 2 * LABEL_PADDING);
            }

            const unitDirection = { x: direction.x / dist, y: direction.y / dist };
            let labelLeft: number, labelTop: number;

            if (Math.abs(unitDirection.x) > Math.abs(unitDirection.y)) {
                labelLeft = unitDirection.x > 0 ? point4.x : point4.x - dynamicWidth;
                labelTop = point4.y - dynamicHeight / 2;
            } else {
                labelTop = unitDirection.y > 0 ? point4.y : point4.y - dynamicHeight;
                labelLeft = point4.x - dynamicWidth / 2;
            }

            return { left: labelLeft, top: labelTop, width: dynamicWidth, height: dynamicHeight };
        };

        // 辅助函数：寻找最佳标签位置
        const findBestLabelCandidate = (targetPoint: { x: number; y: number }, food: FoodData): LabelCandidate | null => {
            const candidates: LabelCandidate[] = [];
            const directions = [
                { x: 1, y: 0 },
                { x: -1, y: 0 },
                { x: 0, y: 1 },
                { x: 0, y: -1 },
                { x: 1, y: 1 },
                { x: -1, y: 1 },
                { x: 1, y: -1 },
                { x: -1, y: -1 }
            ];

            for (const dir of directions) {
                for (let dist = MIN_DISTANCE_FROM_POINT; dist <= 250; dist += 10) {
                    const point4 = {
                        x: targetPoint.x + dir.x * dist,
                        y: targetPoint.y + dir.y * dist
                    };
                    const labelRect = calculateLabelRectFromPoint4(point4, targetPoint, food);
                    if (labelRect && isRectWithinParent(labelRect) && !isRectOverlapping(labelRect)) {
                        candidates.push({ point2: point4, labelRect });
                    }
                }
            }

            if (candidates.length === 0) return null;

            // 排序候选位置
            candidates.sort((a, b) => {
                const scoreA = calculateCandidateScore(a.labelRect, targetPoint);
                const scoreB = calculateCandidateScore(b.labelRect, targetPoint);

                if (scoreA !== scoreB) {
                    return scoreB - scoreA;
                } else {
                    const distA = distance(a.point2, targetPoint);
                    const distB = distance(b.point2, targetPoint);
                    return distA - distB;
                }
            });

            return candidates[0];
        };

        // 辅助函数：绘制标签
        const drawLabel = (
            rect: { left: number; top: number; width: number; height: number },
            food: FoodData
        ) => {
            // 绘制背景
            ctx.fillStyle = LABEL_BG_COLOR;
            ctx.beginPath();
            ctx.roundRect(rect.left, rect.top, rect.width, rect.height, 8);
            ctx.fill();

            // 绘制边框
            ctx.strokeStyle = '#D3D3D3';
            ctx.lineWidth = 1;
            ctx.stroke();

            const foodName = food.name;
            const nutrition = food.nutrition;

            const carbsText = `碳 ${nutrition.carbs}g`;
            const proteinText = `蛋 ${nutrition.protein}g`;
            const fatText = `脂 ${nutrition.fat}g`;

            // 测量文字尺寸
            ctx.font = '12px SourceHanSerifCN, sans-serif';
            ctx.fillStyle = TITLE_TEXT_COLOR;
            const titleHeight = 15; // 估算高度

            ctx.font = '8px sans-serif';
            ctx.fillStyle = NUTRITION_TEXT_COLOR;
            const carbsMetrics = ctx.measureText(carbsText);
            const proteinMetrics = ctx.measureText(proteinText);
            const nutritionHeight = 10; // 估算高度

            let currentX = rect.left + LABEL_PADDING;
            let totalHeight = titleHeight;
            if (titleIcon) {
                totalHeight = Math.max(totalHeight, TITLE_ICON_HEIGHT);
            }
            const contentTotalHeight = totalHeight + TITLE_NUTRITION_SPACING + nutritionHeight;
            const contentStartY = rect.top + (rect.height - contentTotalHeight) / 2;

            // 绘制标题图标
            if (titleIcon) {
                const iconScale = TITLE_ICON_HEIGHT / titleIcon.height;
                const iconWidth = titleIcon.width * iconScale;
                const iconY = contentStartY + (totalHeight - TITLE_ICON_HEIGHT) / 2;

                ctx.drawImage(
                    titleIcon,
                    currentX,
                    iconY,
                    iconWidth,
                    TITLE_ICON_HEIGHT
                );

                currentX += iconWidth + ICON_TEXT_SPACING;
            }

            // 绘制标题文字
            const titleY = contentStartY + (totalHeight - titleHeight) / 2 + titleHeight * 0.8;
            ctx.font = '12px SourceHanSerifCN, sans-serif';
            ctx.fillStyle = TITLE_TEXT_COLOR;
            ctx.fillText(foodName, currentX, titleY);

            // 绘制营养信息
            const nutritionY = titleY + TITLE_NUTRITION_SPACING + nutritionHeight * 0.8;
            currentX = rect.left + LABEL_PADDING;

            ctx.font = '8px sans-serif';
            ctx.fillStyle = NUTRITION_TEXT_COLOR;

            ctx.fillText(carbsText, currentX, nutritionY);
            currentX += carbsMetrics.width + NUTRITION_ITEM_SPACING;

            ctx.fillText(proteinText, currentX, nutritionY);
            currentX += proteinMetrics.width + NUTRITION_ITEM_SPACING;

            ctx.fillText(fatText, currentX, nutritionY);
        };

        // 辅助函数：绘制弧线和箭头
        const drawArcAndArrow = (
            point3: { x: number; y: number },
            point4: { x: number; y: number },
            labelRect: { left: number; top: number; width: number; height: number }
        ) => {
            const vector = { x: point4.x - point3.x, y: point4.y - point3.y };
            const length = distance(point4, point3);
            if (length < 0.001) return;

            const midPoint = { x: (point3.x + point4.x) / 2, y: (point3.y + point4.y) / 2 };
            const normal = { x: -vector.y / length, y: vector.x / length };
            const maxArcDegree = 60;
            const maxRadians = (maxArcDegree * Math.PI) / 180;
            const controlOffset = (length * Math.tan(maxRadians / 2)) / 2;
            const controlPoint = {
                x: midPoint.x + normal.x * controlOffset,
                y: midPoint.y + normal.y * controlOffset
            };

            // 绘制贝塞尔曲线
            ctx.strokeStyle = ARC_COLOR;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(point3.x, point3.y);
            ctx.quadraticCurveTo(controlPoint.x, controlPoint.y, point4.x, point4.y);
            ctx.stroke();

            // 计算箭头角度
            const tangentVector = { x: point4.x - controlPoint.x, y: point4.y - controlPoint.y };
            const labelCenter = {
                x: labelRect.left + labelRect.width / 2,
                y: labelRect.top + labelRect.height / 2
            };
            const labelDirectionVector = { x: labelCenter.x - point4.x, y: labelCenter.y - point4.y };

            const tangentWeight = 0.3;
            const labelDirWeight = 0.1;
            const combinedVector = {
                x: tangentVector.x * tangentWeight + labelDirectionVector.x * labelDirWeight,
                y: tangentVector.y * tangentWeight + labelDirectionVector.y * labelDirWeight
            };

            const combinedLength = Math.sqrt(combinedVector.x ** 2 + combinedVector.y ** 2);
            const rotationAngle = combinedLength < 0.001
                ? Math.atan2(tangentVector.y, tangentVector.x)
                : Math.atan2(combinedVector.y, combinedVector.x);

            // 绘制箭头
            ctx.beginPath();
            ctx.moveTo(point4.x, point4.y);
            ctx.lineTo(
                point4.x - ARROW_LENGTH * Math.cos(rotationAngle - ARROW_ANGLE),
                point4.y - ARROW_LENGTH * Math.sin(rotationAngle - ARROW_ANGLE)
            );
            ctx.moveTo(point4.x, point4.y);
            ctx.lineTo(
                point4.x - ARROW_LENGTH * Math.cos(rotationAngle + ARROW_ANGLE),
                point4.y - ARROW_LENGTH * Math.sin(rotationAngle + ARROW_ANGLE)
            );
            ctx.stroke();

            return { start: point3, end: point4, control: controlPoint };
        };

        // 遍历所有食物，绘制标签和连接线
        foodData.foods.forEach((food, i) => {
            const bbox = food.bbox_2d;

            // 转换为原始像素坐标
            const x1Pixel = bbox[0] * originalImageSize.width;
            const y1Pixel = bbox[1] * originalImageSize.height;
            const x2Pixel = bbox[2] * originalImageSize.width;
            const y2Pixel = bbox[3] * originalImageSize.height;

            // 转换为画布坐标
            const centerPoint = {
                x: (x1Pixel + x2Pixel) / 2 * scaleX + imageOffsetX,
                y: (y1Pixel + y2Pixel) / 2 * scaleY + imageOffsetY
            };

            const bestCandidate = findBestLabelCandidate(centerPoint, food);

            if (!bestCandidate) return;

            drawLabel(bestCandidate.labelRect, food);
            const arcInfo = drawArcAndArrow(centerPoint, bestCandidate.point2, bestCandidate.labelRect);
            if (arcInfo) {
                drawnArcs.push(arcInfo);
            }
            placedLabels.push(bestCandidate.labelRect);
        });
    }, [image, foodData, originalImageSize, titleIcon]);

    // 当图片加载完成后绘制
    useEffect(() => {
        if (image && !isLoading && !hasError) {
            drawCanvas();
        }
    }, [image, isLoading, hasError, drawCanvas]);

    // 处理窗口大小变化
    useEffect(() => {
        const handleResize = () => {
            if (canvasRef.current && image) {
                drawCanvas();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [image, drawCanvas]);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <div>加载中...</div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <div>图片加载失败，请重试</div>
            </div>
        );
    }

    return (
        <canvas
            ref={canvasRef}
            width={800 * HIGH_DPI_SCALE}
            height={600 * HIGH_DPI_SCALE}
            style={{
                width: '100%',
                height: '100%',
                maxWidth: '800px',
                maxHeight: '600px'
            }}
        />
    );
};